import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDocumentTagType } from "./enums/enumDocumentTagType.generated";
import { EnumDocumentType } from "./enums/enumDocumentType.generated";
import { EnumDocumentTypeGroup } from "./enums/enumDocumentTypeGroup.generated";

// Model Objects

export class AvailableTag extends ModelObject<AvailableTag> {

    public tagKey: string = "";
    public tagType: EnumDocumentTagType = EnumDocumentTagType.string;
    public hideable: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tagKey = jsonSerializedObject.tagKey;
          this.tagType = EnumDocumentTagType.getByValue(jsonSerializedObject.tagType, this.tagType);
          this.hideable = jsonSerializedObject.hideable;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tagKey = this.tagKey;
        toRet.tagType = this.tagType.value;
        toRet.hideable = this.hideable;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class TagOverride extends ModelObject<TagOverride> {

    public tagKey: string = "";
    public hidden: boolean = false;
    /**
    * For images use a Base64Encoded string
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public overrideValue: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tagKey = jsonSerializedObject.tagKey;
          this.hidden = jsonSerializedObject.hidden;
          this.overrideValue = jsonSerializedObject.overrideValue;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tagKey = this.tagKey;
        toRet.hidden = this.hidden;
        toRet.overrideValue = this.overrideValue;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DocumentTemplateReadOnly extends ModelObject<DocumentTemplateReadOnly> {

    public availableTags: AvailableTag[] = [];
    /**
    * Whether this template is locked for modification
    */
    public isLocked: boolean = false;
    /**
    * Timestamp at which the template was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Timestamp at which the template was last updated on the system. Captured by system not user editable.
    */
    public lastUpdatedAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Whether this document is available based on the tenant's country location
    */
    public isAvailableForTenant: boolean = false;
    /**
    * Used for grouping document types
    */
    public documentTypeGroup: EnumDocumentTypeGroup = EnumDocumentTypeGroup.other;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.availableTags = jsonSerializedObject.availableTags.map((v: any) => new AvailableTag(v));
          this.isLocked = jsonSerializedObject.isLocked;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.lastUpdatedAt = Instant.parse(jsonSerializedObject.lastUpdatedAt);
          this.isAvailableForTenant = jsonSerializedObject.isAvailableForTenant;
          this.documentTypeGroup = EnumDocumentTypeGroup.getByValue(jsonSerializedObject.documentTypeGroup, this.documentTypeGroup);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.availableTags = this.availableTags.map(v => v.toJsonSerializedObject());
        toRet.isLocked = this.isLocked;
        toRet.createdAt = this.createdAt.toString();
        toRet.lastUpdatedAt = this.lastUpdatedAt.toString();
        toRet.isAvailableForTenant = this.isAvailableForTenant;
        toRet.documentTypeGroup = this.documentTypeGroup.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "availableTags", rule: (v: any) => !!v || "availableTags is required"},
            {field: "availableTags", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as AvailableTag[]).reduce((acc: Array<string>, curr: AvailableTag) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class EmailDetails extends ModelObject<EmailDetails> {

    public emailBcc: string = "";
    public emailSubject: string = "";
    public emailBody: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.emailBcc = jsonSerializedObject.emailBcc;
          this.emailSubject = jsonSerializedObject.emailSubject;
          this.emailBody = jsonSerializedObject.emailBody;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.emailBcc = this.emailBcc;
        toRet.emailSubject = this.emailSubject;
        toRet.emailBody = this.emailBody;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DocumentTemplate extends ModelObject<DocumentTemplate> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Template type
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespce.
    */
    public name: string = "";
    /**
    * Template type
    */
    public documentType: EnumDocumentType = EnumDocumentType.none;
    /**
    * Template file id
    */
    public documentTemplateFileId: Guid = Guid.createEmpty();
    /**
    * Whether this is default document template for this type
    */
    public isDocumentTypeDefault: boolean = false;
    public tagOverrides: TagOverride[] = [];
    public emailDetails: EmailDetails = new EmailDetails();
    public readOnly: DocumentTemplateReadOnly = new DocumentTemplateReadOnly();
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.documentType = EnumDocumentType.getByValue(jsonSerializedObject.documentType, this.documentType);
          this.documentTemplateFileId = Guid.parseOrEmpty(jsonSerializedObject.documentTemplateFileId);
          this.isDocumentTypeDefault = jsonSerializedObject.isDocumentTypeDefault;
          this.tagOverrides = jsonSerializedObject.tagOverrides.map((v: any) => new TagOverride(v));
          this.emailDetails = new EmailDetails(jsonSerializedObject.emailDetails);
          this.readOnly = new DocumentTemplateReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.documentType = this.documentType.value;
        toRet.documentTemplateFileId = this.documentTemplateFileId.toString();
        toRet.isDocumentTypeDefault = this.isDocumentTypeDefault;
        toRet.tagOverrides = this.tagOverrides.map(v => v.toJsonSerializedObject());
        toRet.emailDetails = this.emailDetails.toJsonSerializedObject();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "DocumentTemplate.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "documentTemplateFileId", rule: (v: any) => !v.isEmpty() || "DocumentTemplate.DocumentTemplateFileId cannot be empty"},
            {field: "tagOverrides", rule: (v: any) => !!v || "tagOverrides is required"},
            {field: "tagOverrides", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as TagOverride[]).reduce((acc: Array<string>, curr: TagOverride) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "emailDetails", rule: (v: any) => !!v || "emailDetails is required"},
            {field: "emailDetails", rule: (v: any) => v.validate()},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class FilteredDocumentTemplateResult extends ModelObject<FilteredDocumentTemplateResult> {

    public matchingCount: number = 0;
    public documentTemplates: DocumentTemplate[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.documentTemplates = jsonSerializedObject.documentTemplates.map((v: any) => new DocumentTemplate(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.documentTemplates = this.documentTemplates.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "documentTemplates", rule: (v: any) => !!v || "documentTemplates is required"},
            {field: "documentTemplates", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as DocumentTemplate[]).reduce((acc: Array<string>, curr: DocumentTemplate) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class DocumentTemplateFilter extends ModelObject<DocumentTemplateFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _documentTypes: EnumDocumentType[] | null = null;
    public get documentTypes(): EnumDocumentType[] | null { return this._documentTypes }
    public set documentTypes(value: EnumDocumentType[] | null) {
      this._documentTypes = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.documentTypes = jsonSerializedObject.documentTypes == null ? null : jsonSerializedObject.documentTypes.map((v: any) => EnumDocumentType.getByValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.documentTypes = this.documentTypes == null ? null : this.documentTypes.map(v => v.value);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class DocumentTemplateService extends ServiceBase {

  public async getAllDocumentTemplates(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DocumentTemplate[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.documentTemplate.map((v: any) => new DocumentTemplate(v));;
    return this.makeJsonRequest("documentTemplate/v1/getAllDocumentTemplates", req, false, extractor, opts);
  };

  public async getDocumentTemplate(tenantId: Guid, documentTemplateId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DocumentTemplate> {
    const req = {
    tenantId:tenantId.toString(),
    documentTemplateId:documentTemplateId.toString(),
    };
    const extractor = (response:any) => new DocumentTemplate(response.documentTemplate);;
    return this.makeJsonRequest("documentTemplate/v1/getDocumentTemplate", req, false, extractor, opts);
  };

  public async getTemplateIdOfTypeDefault(tenantId: Guid, documentType: EnumDocumentType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Guid | null> {
    const req = {
    tenantId:tenantId.toString(),
    documentType:documentType.value,
    };
    const extractor = (response:any) => response.documentId == null ? null : Guid.parseOrEmpty(response.documentId);;
    return this.makeJsonRequest("documentTemplate/v1/getTemplateIdOfTypeDefault", req, false, extractor, opts);
  };

  public async getRequiredTypeDefaultTemplate(tenantId: Guid, documentType: EnumDocumentType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DocumentTemplate> {
    const req = {
    tenantId:tenantId.toString(),
    documentType:documentType.value,
    };
    const extractor = (response:any) => new DocumentTemplate(response.documentTemplate);;
    return this.makeJsonRequest("documentTemplate/v1/getRequiredTypeDefaultTemplate", req, false, extractor, opts);
  };

  public async saveDocumentTemplate(tenantId: Guid, documentTemplate: DocumentTemplate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    documentTemplate:documentTemplate.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("documentTemplate/v1/saveDocumentTemplate", req, true, extractor, opts);
  };

  public async setDocumentTemplateIsHidden(tenantId: Guid, documentTemplateId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    documentTemplateId:documentTemplateId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("documentTemplate/v1/setDocumentTemplateIsHidden", req, true, extractor, opts);
  };

  public async getEmailDetails(tenantId: Guid, documentType: EnumDocumentType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<EmailDetails> {
    const req = {
    tenantId:tenantId.toString(),
    documentType:documentType.value,
    };
    const extractor = (response:any) => new EmailDetails(response.emailDetails);;
    return this.makeJsonRequest("documentTemplate/v1/getEmailDetails", req, false, extractor, opts);
  };

  public async getDocumentTemplatesByFilter(tenantId: Guid, filter: DocumentTemplateFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredDocumentTemplateResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredDocumentTemplateResult(response.results);;
    return this.makeJsonRequest("documentTemplate/v1/getDocumentTemplatesByFilter", req, false, extractor, opts);
  };
}

export const documentTemplateService = new DocumentTemplateService();